import { useEffect, useRef, useState, useCallback } from "react";

// styles 
import styles from "./styles/Notification.module.css";

// hooks 
import { useNavigate } from "react-router-dom";
import { useNotificationStore } from "@/state/useNotificationStore";

// functions 
import { navigateFromDeeplink } from "@/navigation/navigateFromDeeplink";

// types 
import type { AppNotification } from "@/types/appNotification";



const ICONS: Record<string, string> = {
    order: "bi-cart",
    payment: "bi-credit-card",
    alert: "bi-exclamation-triangle",
    default: "bi-bell",
};

function formatTime(ts: number): string {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

const PAGE_SIZE = 50;


export default function Notification() {
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(1);
    const panelRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);

    const notifications = useNotificationStore((s) => s.notifications);
    const unread_count = useNotificationStore((s) => s.unread_count);
    const markAsRead = useNotificationStore((s) => s.markAsRead);
    const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
    const clearNotifications = useNotificationStore((s) => s.clearNotifications);

    // reset page when panel closes
    useEffect(() => {
        if (!open) setPage(1);
    }, [open]);

    // close on outside click
    useEffect(() => {
        function handler(e: MouseEvent) {
            if (
                open &&
                panelRef.current &&
                !panelRef.current.contains(e.target as Node) &&
                btnRef.current &&
                !btnRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const displayed = notifications.slice(0, page * PAGE_SIZE);
    const hasMore = displayed.length < notifications.length;

    const onClickItem = useCallback((item: AppNotification) => {
        if (!item.is_read) markAsRead(item.id);
        if (item.data?.deeplink) {
            navigateFromDeeplink(item.data?.deeplink, navigate);
        }
    }, [markAsRead, navigate]);

    const loadMore = useCallback(() => {
        setPage((p) => p + 1);
    }, []);

    return (
        <div style={{ position: "relative" }}>

            {/* bell button */}
            <button
                ref={btnRef}
                onClick={() => setOpen((o) => !o)}
                aria-label="Notifications"
                className={styles.bellBtn}
            >
                <i className="bi bi-bell" />
                {!!unread_count && (
                    <span className={styles.badge}>
                        {unread_count > 99 ? "99+" : unread_count}
                    </span>
                )}
            </button>

            {/* dropdown panel */}
            {open && (
                <div ref={panelRef} className={styles.panel}>

                    {/* panel header */}
                    <div className={styles.panelHeader}>
                        <span className={styles.panelTitle}>
                            Notifications
                            {unread_count > 0 && (
                                <span className={styles.panelTitleBadge}>
                                    {unread_count}
                                </span>
                            )}
                        </span>
                        {notifications.length > 0 && (
                            <div className={styles.headerActions}>
                                <button onClick={markAllAsRead} className={styles.markAllBtn}>
                                    Mark all read
                                </button>
                                <button onClick={clearNotifications} className={styles.clearBtn}>
                                    Clear all
                                </button>
                            </div>
                        )}
                    </div>

                    {/* notification list */}
                    <div className={styles.list}>
                        {notifications.length === 0 ? (
                            <div className={styles.empty}>
                                <i className={`bi bi-bell-slash ${styles.emptyIcon}`} />
                                No notifications
                            </div>
                        ) : (
                            <>
                                {displayed.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => onClickItem(item)}
                                        className={`${styles.notifItem} ${!item.is_read ? styles.unread : ""}`}
                                    >
                                        <span className={`${styles.unreadDot} ${!item.is_read ? styles.active : ""}`} />

                                        <div className={styles.iconWrap}>
                                            <i
                                                className={`bi ${ICONS[item.type ?? ""] ?? ICONS.default} ${styles.notifIcon}`}
                                            />
                                        </div>

                                        <div className={styles.notifContent}>
                                            <div className={styles.notifTitle}>{item.title}</div>
                                            <div className={styles.notifBody}>{item.body}</div>
                                            <div className={styles.notifTime}>{formatTime(item.created_at)}</div>
                                        </div>
                                    </div>
                                ))}

                                {hasMore && (
                                    <div className={styles.loadMoreWrap}>
                                        <button onClick={loadMore} className={styles.loadMoreBtn}>
                                            Load more
                                            <span className={styles.loadMoreCount}>
                                                ({notifications.length - displayed.length} remaining)
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}